<template>
  <el-dialog
      v-model="dialogVisible"
      :title="isEditing ? $t('account_setting.receive_rule.edit') : $t('account_setting.receive_rule.create')"
      width="920px"
      append-to-body
      :close-on-click-modal="false"
      class="receive-rule-dialog"
  >
    <div class="rule-editor">
      <div class="rule-editor__section">
        <div class="rule-editor__section-title">
          <span class="rule-editor__required">*</span>
          {{ $t('account_setting.receive_rule.when_arrives') }}
          <el-select v-model="form.conditionJoin" size="small" class="rule-editor__join-select">
            <el-option :label="$t('account_setting.receive_rule.all_conditions')" value="all" />
            <el-option :label="$t('account_setting.receive_rule.any_condition')" value="any" />
          </el-select>
        </div>

        <div v-for="(condition, index) in form.conditions" :key="index" class="rule-editor__condition">
          <el-select
              v-model="condition.field"
              size="small"
              class="rule-editor__field-select"
              @change="handleFieldChange(condition, index)"
          >
            <el-option
                v-for="item in fieldOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
          <el-select
              v-model="condition.operator"
              size="small"
              class="rule-editor__operator-select"
              @change="handleOperatorChange(condition)"
          >
            <el-option
                v-for="item in getOperatorOptions(condition)"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>

          <template v-if="!isBooleanField(condition)">
            <div
                class="rule-editor__condition-value"
                :class="{'rule-editor__condition-value--has-picker-trigger': ['customer', 'contact'].includes(getFieldConfig(condition.field).picker)}"
            >
              <el-select
                  :model-value="getConditionInputValue(condition)"
                  size="small"
                  class="rule-editor__condition-value-select"
                  :multiple="!isEqualsOperator(condition)"
                  value-key="value"
                  filterable
                  :allow-create="!requiresPrimaryKeyValue(condition)"
                  default-first-option
                  clearable
                  :reserve-keyword="false"
                  :placeholder="getConditionPlaceholder(condition)"
                  :loading="isTagLoading(index)"
                  @visible-change="loadTagOptionsOnVisible(condition, index, $event)"
                  @update:model-value="updateConditionValues(condition, $event)"
              >
                <el-option
                    v-for="option in getConditionOptions(condition, index)"
                    :key="option.value"
                    :label="option.label"
                    :value="requiresPrimaryKeyValue(condition) ? option : option.value"
                />
              </el-select>
              <el-button
                  v-if="getFieldConfig(condition.field).picker === 'customer'"
                  text
                  class="rule-editor__condition-picker-trigger"
                  :aria-label="$t('account_setting.receive_rule.add_archived_customer')"
                  @mousedown.stop
                  @click.stop="openCustomerSelector(index)"
              >
                <el-icon><User /></el-icon>
              </el-button>
              <el-button
                  v-else-if="getFieldConfig(condition.field).picker === 'contact'"
                  text
                  class="rule-editor__condition-picker-trigger"
                  :aria-label="$t('account_setting.receive_rule.add_archived_contact')"
                  @mousedown.prevent.stop
                  @click.stop="openContactSelector(index)"
              >
                <el-icon><UserFilled /></el-icon>
              </el-button>
            </div>
          </template>

          <div class="rule-editor__row-actions">
            <el-button circle text :disabled="form.conditions.length === 1" @click="removeCondition(index)">
              <el-icon><Remove /></el-icon>
            </el-button>
            <el-button circle text @click="addCondition">
              <el-icon><CirclePlus /></el-icon>
            </el-button>
          </div>
        </div>
      </div>

      <div class="rule-editor__section">
        <div class="rule-editor__section-title">
          <span class="rule-editor__required">*</span>{{ $t('account_setting.receive_rule.then') }}
        </div>
        <div class="rule-editor__action">
          <el-select v-model="form.action" size="small" class="rule-editor__action-select">
            <el-option :label="$t('account_setting.receive_rule.move_to')" value="move" />
            <el-option :label="$t('account_setting.receive_rule.mark_as')" value="mark" />
            <el-option :label="$t('account_setting.receive_rule.forward_to')" value="forward" />
          </el-select>

          <div
              v-if="form.action === 'move'"
              class="rule-editor__folder-action"
          >
            <el-select
                v-model="form.targetFolder"
                size="small"
                class="rule-editor__folder-select"
                :placeholder="$t('account_setting.receive_rule.select_folder')"
                popper-class="receive-rule-folder-dropdown"
            >
              <template v-if="form.targetFolder" #prefix>
                <el-icon class="rule-editor__folder-prefix"><Folder /></el-icon>
              </template>
              <el-option
                  v-for="item in folderOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
              >
                <span class="rule-editor__folder-option">
                  <el-icon><Folder /></el-icon>
                  <span>{{ item.label }}</span>
                </span>
              </el-option>
              <template #footer>
                <div v-if="supportsFolderFooter" class="rule-editor__folder-create" @click.stop @mousedown.stop>
                  <el-input
                      v-model.trim="newFolderName"
                      size="small"
                      class="rule-editor__folder-create-input"
                      :placeholder="$t('account_setting.receive_rule.folder_name_placeholder')"
                      maxlength="15"
                      @keydown.stop
                      @keyup.enter.stop="createFolder"
                  >
                    <template #suffix>
                      <el-button
                          link
                          type="primary"
                          :loading="folderCreating"
                          :disabled="!newFolderName || folderCreating"
                          @click.stop="createFolder"
                      >
                        {{ $t('common.save') }}
                      </el-button>
                    </template>
                  </el-input>
                </div>
              </template>
            </el-select>
            <div v-if="!supportsFolderFooter" class="rule-editor__folder-create rule-editor__folder-create--fallback">
              <el-input
                  v-model.trim="newFolderName"
                  size="small"
                  class="rule-editor__folder-create-input"
                  :placeholder="$t('account_setting.receive_rule.folder_name_placeholder')"
                  maxlength="15"
                  @keyup.enter="createFolder"
              >
                <template #suffix>
                  <el-button
                      link
                      type="primary"
                      :loading="folderCreating"
                      :disabled="!newFolderName || folderCreating"
                      @click="createFolder"
                  >
                    {{ $t('common.save') }}
                  </el-button>
                </template>
              </el-input>
            </div>
          </div>

          <el-select
              v-else-if="form.action === 'mark'"
              v-model="form.markActions"
              size="small"
              multiple
              clearable
              class="rule-editor__action-control rule-editor__mark-select"
              :placeholder="$t('account_setting.receive_rule.select_mark')"
          >
            <el-option :label="$t('account_setting.receive_rule.read_mail')" value="read">
              <span class="rule-editor__mark-option">
                <el-icon><Message /></el-icon>
                <span>{{ $t('account_setting.receive_rule.read_mail') }}</span>
              </span>
            </el-option>
            <el-option :label="$t('account_setting.receive_rule.starred_mail')" value="starred">
              <span class="rule-editor__mark-option">
                <el-icon><Star /></el-icon>
                <span>{{ $t('account_setting.receive_rule.starred_mail') }}</span>
              </span>
            </el-option>
          </el-select>

          <el-select
              v-else
              v-model="form.forwardEmails"
              size="small"
              class="rule-editor__action-control"
              multiple
              filterable
              allow-create
              default-first-option
              clearable
              :reserve-keyword="false"
              :placeholder="$t('account_setting.receive_rule.forward_placeholder')"
              :loading="forwardMemberLoading"
              @visible-change="loadForwardMembers"
          >
            <el-option
                v-for="member in forwardMemberOptions"
                :key="getForwardMemberEmail(member)"
                :label="getForwardMemberLabel(member)"
                :value="getForwardMemberEmail(member)"
            />
          </el-select>
        </div>
      </div>

      <div class="rule-editor__name">
        <label for="receive-rule-name">{{ $t('account_setting.receive_rule.name') }}</label>
        <div class="rule-editor__name-input">
          <el-input
              id="receive-rule-name"
              v-model.trim="form.ruleName"
              size="small"
              :placeholder="defaultRuleName"
              maxlength="100"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <el-button type="primary" @click="save(true)">
        {{ $t('account_setting.receive_rule.save_enable') }}
      </el-button>
      <el-button @click="save(false)">
        {{ $t('account_setting.receive_rule.save_only') }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
      v-model="customerSelectorVisible"
      :title="$t('account_setting.receive_rule.select_archived_customer')"
      width="780px"
      append-to-body
      :close-on-click-modal="false"
      class="customer-picker-dialog"
  >
    <div class="customer-picker">
      <section class="customer-picker__panel">
        <el-input
            v-model="customerSearch"
            size="small"
            clearable
            :placeholder="$t('common.please.input')"
            @keyup.enter="searchArchivedCustomers"
        >
          <template #suffix>
            <el-icon class="customer-picker__search" @click="searchArchivedCustomers"><Search /></el-icon>
          </template>
        </el-input>
        <div v-loading="customerLoading" class="customer-picker__list" @scroll="loadMoreArchivedCustomers">
          <button
              v-for="customer in customerOptions"
              :key="getCustomerListKey(customer)"
              type="button"
              class="customer-picker__item"
              :disabled="!getCustomerSelectionValue(customer) || isCustomerSelected(customer)"
              @click="addArchivedCustomer(customer)"
          >
            <el-popover
                placement="left"
                :width="'max-content'"
                trigger="hover"
                :content="customer.email"
                effect="dark"
                :show-after="1200"
            >
              <template #reference>
                <span class="customer-picker__customer-name">
                  <span>{{ getCustomerNameParts(customer).name }}</span>
                  <span v-if="getCustomerNameParts(customer).company" class="customer-picker__customer-separator">-</span>
                  <span v-if="getCustomerNameParts(customer).company" class="customer-picker__customer-company">
                    {{ getCustomerNameParts(customer).company }}
                  </span>
                </span>
              </template>
            </el-popover>
            <el-icon><Plus /></el-icon>
          </button>
          <el-empty
              v-if="!customerLoading && !customerOptions.length"
              :description="$t('common.data.empty')"
              :image-size="48"
          />
        </div>
      </section>

      <section class="customer-picker__panel">
        <div class="customer-picker__selected-header">
          <span>
            {{ selectedCustomerCountLabelParts[0] }}<em>{{ selectedCustomers.length }}</em>{{ selectedCustomerCountLabelParts[1] }}
          </span>
          <el-button link type="primary" :disabled="!selectedCustomers.length" @click="clearSelectedCustomers">
            {{ $t('common.empty') }}
          </el-button>
        </div>
        <div class="customer-picker__list">
          <div
              v-for="customer in selectedCustomers"
              :key="getCustomerSelectionValue(customer)"
              class="customer-picker__item customer-picker__item--selected"
          >
            <span>{{ getCustomerSelectionLabel(customer) }}</span>
            <el-button link type="danger" :aria-label="$t('common.delete')" @click="removeArchivedCustomer(customer)">
              <el-icon><Close /></el-icon>
            </el-button>
          </div>
          <el-empty
              v-if="!selectedCustomers.length"
              :description="$t('common.data.empty')"
              :image-size="48"
          />
        </div>
      </section>
    </div>
    <template #footer>
      <el-button type="primary" @click="confirmSelectedCustomers">
        {{ $t('common.confirm') }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
      v-model="contactSelectorVisible"
      :title="$t('account_setting.receive_rule.add_archived_contact')"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      class="rule-picker-dialog"
  >
    <div class="rule-picker-form">
      <label>{{ $t('account_setting.receive_rule.select_customer') }}</label>
      <el-select
          v-model="contactCustomerId"
          filterable
          remote
          clearable
          size="small"
          :loading="contactCustomerLoading"
          :remote-method="searchContactCustomers"
          :placeholder="$t('account_setting.receive_rule.select_customer')"
          @visible-change="loadContactCustomersOnVisible"
          @change="handleContactCustomerChange"
      >
        <el-option
            v-for="customer in contactCustomerOptions"
            :key="customer.id"
            :label="getCustomerSelectionLabel(customer)"
            :value="customer.id"
        />
      </el-select>

      <label>{{ $t('account_setting.receive_rule.select_contact') }}</label>
      <el-select
          :model-value="contactPickerValues"
          multiple
          filterable
          clearable
          size="small"
          :disabled="!contactCustomerId"
          :loading="contactLoading"
          :placeholder="contactCustomerId ? $t('account_setting.receive_rule.select_contact') : $t('account_setting.receive_rule.choose_customer_first')"
          @update:model-value="updateContactPickerValues"
      >
        <el-option
            v-for="contact in getContactPickerOptions()"
            :key="contact.value"
            :label="contact.label"
            :value="contact.value"
        />
      </el-select>
    </div>
    <template #footer>
      <el-button type="primary" @click="confirmSelectedContacts">
        {{ $t('common.confirm') }}
      </el-button>
    </template>
  </el-dialog>

</template>

<script>
import {
  CirclePlus,
  Close,
  Folder,
  Message,
  Plus,
  Remove,
  Search,
  Star,
  User,
  UserFilled
} from '@element-plus/icons-vue'
import {ElMessage, version as elementPlusVersion} from 'element-plus'
import {
  getEmailCustomer,
  getMailCustomerContactByCustomerId,
  getMailCustomerLevelList,
  mailCustomerLabelList,
  requestAllCustomer
} from '@/api/customer'
import {addEmailGroupFile} from '@/api/sidebar'
import {getTeamUserEmail} from '@/api/groups'

const CONDITION_SEPARATOR = /[,，;；\n]+/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_CONDITIONS = 20
const [elementPlusMajor = 0, elementPlusMinor = 0, elementPlusPatch = 0] = String(elementPlusVersion || '')
    .split('.')
    .map(item => Number.parseInt(item, 10) || 0)
const supportsSelectFooterSlot = elementPlusMajor > 2 ||
    (elementPlusMajor === 2 && (elementPlusMinor > 4 || (elementPlusMinor === 4 && elementPlusPatch >= 3)))

const normalizeValueList = (value) => {
  const source = Array.isArray(value) ? value : String(value ?? '').split(CONDITION_SEPARATOR)
  return Array.from(new Set(source.map(item => String(item ?? '').trim()).filter(Boolean)))
}

const createDefaultCondition = () => ({
  field: 'sender',
  operator: 'contains',
  value: '',
  values: [],
  valueOptions: []
})

const createDefaultForm = () => ({
  conditionJoin: 'all',
  conditions: [createDefaultCondition()],
  action: 'move',
  targetFolder: '',
  markActions: [],
  forwardEmails: [],
  ruleName: ''
})

export default {
  name: 'ReceiveRuleDialog',
  components: {CirclePlus, Close, Folder, Message, Plus, Remove, Search, Star, User, UserFilled},
  props: {
    modelValue: Boolean,
    rule: {
      type: Object,
      default: null
    },
    folderOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'save'],
  data() {
    return {
      form: createDefaultForm(),
      newFolderName: '',
      folderCreating: false,
      folderCreateRequestVersion: 0,
      supportsFolderFooter: supportsSelectFooterSlot,
      customerSelectorVisible: false,
      customerSelectorConditionIndex: null,
      customerSelectorField: '',
      customerSearch: '',
      customerLoading: false,
      customerRequestVersion: 0,
      customerOptions: [],
      customerPageNo: 1,
      customerPageSize: 15,
      customerTotalPages: 1,
      selectedCustomers: [],
      contactSelectorVisible: false,
      contactSelectorConditionIndex: null,
      contactCustomerId: '',
      contactCustomerSearch: '',
      contactCustomerLoading: false,
      contactCustomerRequestVersion: 0,
      contactCustomerOptions: [],
      contactCustomerLoadedSearch: null,
      contactCustomerSearchTimer: null,
      contactLoading: false,
      contactRequestVersion: 0,
      contactOptions: [],
      contactPickerValues: [],
      contactSelectionOptions: [],
      contactPickerCustomerId: '',
      tagLoadingConditionIndex: null,
      tagRequestVersion: 0,
      tagOptionsByCondition: {},
      forwardMemberLoading: false,
      forwardMemberRequestVersion: 0,
      forwardMemberOptions: [],
      forwardMembersLoadedTeamId: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    isEditing() {
      return Boolean(this.rule && this.rule.id !== undefined)
    },
    fieldOptions() {
      return [
        {label: this.$t('account_setting.receive_rule.sender'), value: 'sender', type: 'address', picker: 'customer'},
        {label: this.$t('account_setting.receive_rule.sender_domain'), value: 'senderDomain', type: 'text'},
        {label: this.$t('account_setting.receive_rule.recipient'), value: 'recipient', type: 'address', picker: 'customer'},
        {label: this.$t('account_setting.receive_rule.cc'), value: 'cc', type: 'address', picker: 'customer'},
        {label: this.$t('common.subject'), value: 'subject', type: 'text'},
        {label: this.$t('account_setting.receive_rule.body'), value: 'body', type: 'text'},
        {label: this.$t('account_setting.receive_rule.attachment'), value: 'hasAttachment', type: 'boolean'},
        {label: this.$t('account_setting.receive_rule.attachment_name'), value: 'attachmentName', type: 'text'},
        {label: this.$t('account_setting.receive_rule.archived'), value: 'isArchived', type: 'boolean'},
        {label: this.$t('account_setting.receive_rule.archived_customer'), value: 'archivedCustomer', type: 'text', picker: 'customer'},
        {label: this.$t('account_setting.receive_rule.archived_contact'), value: 'archivedContact', type: 'text', picker: 'contact'},
        {label: this.$t('account_setting.receive_rule.customer_level'), value: 'customerLevel', type: 'text', picker: 'tag', tagType: 'level'},
        {label: this.$t('account_setting.receive_rule.customer_source'), value: 'customerSource', type: 'text', picker: 'tag', tagType: 'source'},
        {label: this.$t('account_setting.receive_rule.intention_product'), value: 'intentionProduct', type: 'text', picker: 'tag', tagType: 'product'}
      ]
    },
    teamId() {
      return this.$store.state.email.teamID
    },
    selectedCustomerCountLabelParts() {
      return this.$t('account_setting.receive_rule.selected_customers', {count: '__count__'}).split('__count__')
    },
    defaultRuleName() {
      const joinLabel = this.form.conditionJoin === 'any'
          ? this.$t('account_setting.receive_rule.any')
          : this.$t('account_setting.receive_rule.all')
      const conditions = this.form.conditions
          .map(condition => this.getConditionDescription(condition))
          .filter(Boolean)
          .join(` ${joinLabel} `)
      return this.$t('account_setting.receive_rule.summary_action', {
        conditions,
        action: this.getActionDescription()
      })
    }
  },
  watch: {
    modelValue(visible) {
      if (visible) {
        this.resetForm()
      } else {
        this.folderCreateRequestVersion += 1
        this.folderCreating = false
      }
    },
    rule: {
      deep: true,
      handler() {
        if (this.modelValue) {
          this.resetForm()
        }
      }
    },
    teamId() {
      this.customerRequestVersion += 1
      this.contactCustomerRequestVersion += 1
      this.contactRequestVersion += 1
      this.tagRequestVersion += 1
      this.forwardMemberRequestVersion += 1
      this.folderCreateRequestVersion += 1
      this.customerLoading = false
      this.contactCustomerLoading = false
      this.contactLoading = false
      this.tagLoadingConditionIndex = null
      this.forwardMemberLoading = false
      this.folderCreating = false
      this.forwardMemberOptions = []
      this.forwardMembersLoadedTeamId = ''
      this.newFolderName = ''
      this.customerOptions = []
      this.selectedCustomers = []
      this.customerSelectorField = ''
      this.customerSelectorVisible = false
      this.contactCustomerOptions = []
      this.contactCustomerSearch = ''
      this.contactCustomerLoadedSearch = null
      this.contactOptions = []
      this.contactPickerValues = []
      this.contactSelectionOptions = []
      this.contactCustomerId = ''
      this.contactPickerCustomerId = ''
      clearTimeout(this.contactCustomerSearchTimer)
      this.contactCustomerSearchTimer = null
      this.contactSelectorVisible = false
      this.tagOptionsByCondition = {}
      this.dialogVisible = false
    }
  },
  methods: {
    resetForm() {
      const source = this.rule ? JSON.parse(JSON.stringify(this.rule)) : createDefaultForm()
      const defaults = createDefaultForm()
      this.form = {
        ...defaults,
        ...source,
        conditions: (source.conditions && source.conditions.length ? source.conditions : defaults.conditions)
            .map(condition => this.normalizeCondition(condition)),
        markActions: normalizeValueList(source.markActions),
        forwardEmails: normalizeValueList(source.forwardEmails)
      }
      this.newFolderName = ''
    },
    normalizeCondition(condition) {
      const source = condition || createDefaultCondition()
      const values = normalizeValueList(Array.isArray(source.values) && source.values.length ? source.values : source.value)
      const knownOptions = Array.isArray(source.valueOptions) ? source.valueOptions : []
      return {
        ...createDefaultCondition(),
        ...source,
        values,
        value: values.join(', '),
        valueOptions: values.map(value => {
          const option = knownOptions.find(item => String(item?.value ?? item?.id) === value)
          return option ? {...option, value: String(option.value ?? option.id)} : {value, label: value}
        })
      }
    },
    getFieldConfig(field) {
      return this.fieldOptions.find(item => item.value === field) || this.fieldOptions[0]
    },
    getConditionPlaceholder(condition) {
      if (condition.field === 'senderDomain') {
        return this.$t('account_setting.receive_rule.sender_domain_placeholder')
      }
      const field = this.getFieldConfig(condition.field)
      const key = this.isEqualsOperator(condition)
          ? 'account_setting.receive_rule.condition_value_single'
          : 'account_setting.receive_rule.condition_value_multiple'
      return this.$t(key, {field: field.label})
    },
    getOperatorOptions(condition) {
      if (this.isBooleanField(condition)) {
        return [
          {label: this.$t('account_setting.receive_rule.yes'), value: 'yes'},
          {label: this.$t('account_setting.receive_rule.no'), value: 'no'}
        ]
      }

      const options = [
        {label: this.$t('account_setting.receive_rule.contains'), value: 'contains'},
        {label: this.$t('account_setting.receive_rule.not_contains'), value: 'notContains'}
      ]
      if (this.getFieldConfig(condition.field).type === 'address') {
        options.splice(1, 0, {label: this.$t('account_setting.receive_rule.equals'), value: 'equals'})
      }
      return options
    },
    isBooleanField(condition) {
      return this.getFieldConfig(condition.field).type === 'boolean'
    },
    isEqualsOperator(condition) {
      return condition.operator === 'equals'
    },
    requiresPrimaryKeyValue(condition) {
      return ['archivedCustomer', 'archivedContact', 'customerLevel', 'customerSource', 'intentionProduct']
          .includes(condition.field)
    },
    getConditionValues(condition) {
      return normalizeValueList(Array.isArray(condition?.values) && condition.values.length ? condition.values : condition?.value)
    },
    getConditionInputValue(condition) {
      if (this.requiresPrimaryKeyValue(condition)) {
        const options = Array.isArray(condition.valueOptions) ? condition.valueOptions : []
        return this.isEqualsOperator(condition) ? (options[0] || null) : options
      }
      const values = this.getConditionValues(condition)
      return this.isEqualsOperator(condition) ? (values[0] || '') : values
    },
    getConditionValueOptions(condition) {
      const values = this.getConditionValues(condition)
      const storedOptions = Array.isArray(condition.valueOptions) ? condition.valueOptions : []
      return values.map(value => {
        const option = storedOptions.find(item => String(item?.value ?? item?.id) === value)
        return option || {value, label: value}
      })
    },
    toPrimaryKeyOption(option) {
      if (!option || typeof option !== 'object') {
        return null
      }
      const primaryKey = option.id ?? option.value
      if (primaryKey === undefined || primaryKey === null || String(primaryKey).trim() === '') {
        return null
      }
      const value = String(primaryKey)
      const label = option.label || option.labelName || option.customerLevel || option.name || value
      return {...option, value, label: String(label)}
    },
    getPrimaryKeyOptions(rawValue, suppliedOptions = []) {
      const selected = (Array.isArray(rawValue) ? rawValue : [rawValue])
          .filter(item => item && typeof item === 'object')
      const source = selected.length ? selected : suppliedOptions
      const optionMap = new Map()
      source.forEach(item => {
        const option = this.toPrimaryKeyOption(item)
        if (option) {
          optionMap.set(option.value, option)
        }
      })
      return Array.from(optionMap.values())
    },
    updateConditionValues(condition, rawValue, suppliedOptions = []) {
      const primaryKeyOptions = this.requiresPrimaryKeyValue(condition)
          ? this.getPrimaryKeyOptions(rawValue, suppliedOptions)
          : []
      let values = primaryKeyOptions.length
          ? primaryKeyOptions.map(option => option.value)
          : normalizeValueList(rawValue)
      if (this.isEqualsOperator(condition) && values.length > 1) {
        values = values.slice(0, 1)
        ElMessage.warning(this.$t('account_setting.receive_rule.only_one_email'))
      }

      const existingOptions = Array.isArray(condition.valueOptions) ? condition.valueOptions : []
      condition.values = values
      condition.value = values.join(', ')
      condition.valueOptions = values.map(value => {
        const option = primaryKeyOptions.find(item => item.value === value) ||
            suppliedOptions.find(item => String(item?.value ?? item?.id) === value) ||
            existingOptions.find(item => String(item?.value ?? item?.id) === value)
        return option ? {...option, value: String(option.value ?? option.id)} : {value, label: value}
      })
    },
    handleFieldChange(condition) {
      this.tagRequestVersion += 1
      this.tagLoadingConditionIndex = null
      this.tagOptionsByCondition = {}
      const allowedOperators = this.getOperatorOptions(condition).map(item => item.value)
      condition.operator = allowedOperators.includes(condition.operator) ? condition.operator : allowedOperators[0]
      condition.values = []
      condition.value = ''
      condition.valueOptions = []
      delete condition.contactCustomerId
      delete condition.contactCustomerLabel
    },
    handleOperatorChange(condition) {
      if (!this.isEqualsOperator(condition)) {
        return
      }
      const values = this.getConditionValues(condition)
      if (values.length > 1) {
        this.updateConditionValues(condition, values.slice(0, 1))
        ElMessage.warning(this.$t('account_setting.receive_rule.only_one_email'))
      }
    },
    addCondition() {
      if (this.form.conditions.length >= MAX_CONDITIONS) {
        ElMessage.warning(this.$t('account_setting.receive_rule.max_conditions'))
        return
      }
      this.form.conditions.push(createDefaultCondition())
    },
    removeCondition(index) {
      if (this.form.conditions.length > 1) {
        this.form.conditions.splice(index, 1)
        this.tagRequestVersion += 1
        this.tagLoadingConditionIndex = null
        this.tagOptionsByCondition = {}
      }
    },
    getActionDescription() {
      if (this.form.action === 'mark') {
        const labels = {
          starred: this.$t('account_setting.receive_rule.starred_mail'),
          read: this.$t('account_setting.receive_rule.read_mail')
        }
        return `${this.$t('account_setting.receive_rule.mark_as')}${this.form.markActions.map(item => labels[item]).filter(Boolean).join(', ')}`
      }
      if (this.form.action === 'forward') {
        return `${this.$t('account_setting.receive_rule.forward_to')}${normalizeValueList(this.form.forwardEmails).join(', ')}`
      }
      const folder = this.folderOptions.find(item => String(item.value) === String(this.form.targetFolder))
      return `${this.$t('account_setting.receive_rule.move_to')}${folder ? folder.label : this.$t('account_setting.receive_rule.folder')}`
    },
    getConditionDescription(condition) {
      const field = this.getFieldConfig(condition.field)
      const operator = this.getOperatorOptions(condition).find(item => item.value === condition.operator)
      const values = this.isBooleanField(condition)
          ? []
          : this.getConditionValueOptions(condition)
              .map(option => option.label || option.value)
              .filter(Boolean)
      return `${field.label || ''}${operator ? operator.label : ''}${values.join(', ')}`
    },
    getCustomerNameParts(customer) {
      const name = String(customer?.name || customer?.customerName || customer?.email || '')
      const match = name.match(/^(.+?)\((.+)\)$/)
      return {
        name: match ? match[1] : name,
        company: match ? match[2] : ''
      }
    },
    getCustomerSelectionLabel(customer) {
      const parts = this.getCustomerNameParts(customer)
      return [parts.name, parts.company].filter(Boolean).join(' - ') || customer?.email || String(customer?.id || '')
    },
    getCustomerSelectionValue(customer, field = this.customerSelectorField) {
      const value = field === 'archivedCustomer'
          ? customer?.id ?? customer?.value
          : customer?.email ?? customer?.value
      return value === undefined || value === null ? '' : String(value)
    },
    getCustomerListKey(customer) {
      return String(customer?.id || customer?.email || '')
    },
    toCustomerSelectionOption(customer, field = this.customerSelectorField) {
      const value = this.getCustomerSelectionValue(customer, field)
      return {
        ...customer,
        value,
        label: this.getCustomerSelectionLabel(customer)
      }
    },
    openCustomerSelector(index) {
      const condition = this.form.conditions[index]
      if (!condition) {
        return
      }
      const optionMap = new Map(this.getConditionValueOptions(condition).map(item => [String(item.value), item]))
      this.customerSelectorConditionIndex = index
      this.customerSelectorField = condition.field
      this.customerSearch = ''
      this.customerPageNo = 1
      this.customerTotalPages = 1
      this.selectedCustomers = this.getConditionValues(condition).map(value => {
        const option = optionMap.get(value)
        if (option) {
          return {
            ...option,
            id: this.customerSelectorField === 'archivedCustomer' ? option.id || value : option.id,
            email: option.email || (this.customerSelectorField === 'archivedCustomer' ? '' : value),
            name: option.name || option.customerName || option.label
          }
        }
        return this.customerSelectorField === 'archivedCustomer'
            ? {value, id: value, customerName: value, label: value}
            : {value, email: value, name: value, label: value}
      })
      this.customerSelectorVisible = true
      this.loadArchivedCustomers(true)
    },
    async searchArchivedCustomers() {
      await this.loadArchivedCustomers(true)
    },
    async loadArchivedCustomers(reset = false) {
      if (!this.teamId) {
        return false
      }
      if (reset) {
        this.customerPageNo = 1
        this.customerTotalPages = 1
      }

      const requestTeamId = this.teamId
      const requestVersion = ++this.customerRequestVersion
      this.customerLoading = true
      try {
        const result = this.customerSelectorField === 'archivedCustomer'
            ? await requestAllCustomer({
              teamId: requestTeamId,
              type: 1,
              search: this.customerSearch.trim(),
              pageNo: this.customerPageNo,
              pageSize: this.customerPageSize
            })
            : await getEmailCustomer({
              teamId: requestTeamId,
              customerName: this.customerSearch.trim(),
              pageNo: this.customerPageNo,
              pageSize: this.customerPageSize
            })
        if (result?.code !== 200) {
          if (reset && requestVersion === this.customerRequestVersion && String(requestTeamId) === String(this.teamId)) {
            this.customerOptions = []
          }
          return false
        }
        if (requestVersion !== this.customerRequestVersion || String(requestTeamId) !== String(this.teamId)) {
          return false
        }

        const records = result?.result?.records || []
        if (reset) {
          this.customerOptions = records
        } else {
          const existingKeys = new Set(this.customerOptions.map(item => this.getCustomerListKey(item)))
          this.customerOptions = [...this.customerOptions, ...records.filter(item => !existingKeys.has(this.getCustomerListKey(item)))]
        }
        const pageCount = Number(result?.result?.pages)
        const total = Number(result?.result?.total)
        if (Number.isFinite(pageCount) && pageCount > 0) {
          this.customerTotalPages = pageCount
        } else if (Number.isFinite(total) && total >= 0) {
          this.customerTotalPages = Math.max(1, Math.ceil(total / this.customerPageSize))
        } else {
          this.customerTotalPages = records.length >= this.customerPageSize
              ? this.customerPageNo + 1
              : this.customerPageNo
        }
        return true
      } catch {
        if (reset && requestVersion === this.customerRequestVersion && String(requestTeamId) === String(this.teamId)) {
          this.customerOptions = []
        }
        return false
      } finally {
        if (requestVersion === this.customerRequestVersion) {
          this.customerLoading = false
        }
      }
    },
    async loadMoreArchivedCustomers(event) {
      const element = event?.target
      if (!element || this.customerLoading || this.customerPageNo >= this.customerTotalPages) {
        return
      }
      if (element.scrollTop < element.scrollHeight - element.clientHeight - 24) {
        return
      }
      const requestTeamId = this.teamId
      const previousPageNo = this.customerPageNo
      const nextPageNo = previousPageNo + 1
      this.customerPageNo = nextPageNo
      const loaded = await this.loadArchivedCustomers()
      if (!loaded && String(requestTeamId) === String(this.teamId) && this.customerPageNo === nextPageNo) {
        this.customerPageNo = previousPageNo
      }
    },
    isCustomerSelected(customer) {
      const value = this.getCustomerSelectionValue(customer)
      return this.selectedCustomers.some(item => this.getCustomerSelectionValue(item) === value)
    },
    addArchivedCustomer(customer) {
      if (!this.getCustomerSelectionValue(customer) || this.isCustomerSelected(customer)) {
        return
      }
      this.selectedCustomers.push(this.toCustomerSelectionOption(customer))
    },
    removeArchivedCustomer(customer) {
      const value = this.getCustomerSelectionValue(customer)
      this.selectedCustomers = this.selectedCustomers.filter(item => this.getCustomerSelectionValue(item) !== value)
    },
    clearSelectedCustomers() {
      this.selectedCustomers = []
    },
    confirmSelectedCustomers() {
      const condition = this.form.conditions[this.customerSelectorConditionIndex]
      if (!condition) {
        return
      }
      const options = this.selectedCustomers.map(item => this.toCustomerSelectionOption(item))
      this.updateConditionValues(condition, options.map(item => item.value), options)
      this.customerSelectorVisible = false
    },
    openContactSelector(index) {
      const condition = this.form.conditions[index]
      if (!condition) {
        return
      }
      this.contactSelectorConditionIndex = index
      this.contactCustomerRequestVersion += 1
      this.contactRequestVersion += 1
      clearTimeout(this.contactCustomerSearchTimer)
      this.contactCustomerSearchTimer = null
      this.contactCustomerId = ''
      this.contactPickerCustomerId = ''
      this.contactCustomerSearch = ''
      this.contactCustomerOptions = []
      this.contactCustomerLoadedSearch = null
      this.contactOptions = []
      this.contactPickerValues = []
      this.contactSelectionOptions = []
      this.contactSelectorVisible = true
      this.loadContactCustomers()
    },
    loadContactCustomersOnVisible(visible) {
      const search = this.contactCustomerSearch.trim()
      if (visible && !this.contactCustomerLoading && this.contactCustomerLoadedSearch !== search) {
        this.loadContactCustomers()
      }
    },
    searchContactCustomers(query) {
      const search = String(query || '').trim()
      if (search === this.contactCustomerSearch && (this.contactCustomerLoading || this.contactCustomerLoadedSearch === search)) {
        return
      }

      this.contactCustomerRequestVersion += 1
      this.contactCustomerLoading = false
      this.contactCustomerSearch = search
      clearTimeout(this.contactCustomerSearchTimer)
      this.contactCustomerSearchTimer = setTimeout(() => {
        this.loadContactCustomers()
      }, 250)
    },
    async loadContactCustomers() {
      if (!this.teamId) {
        return
      }
      const requestTeamId = this.teamId
      const requestVersion = ++this.contactCustomerRequestVersion
      const requestSearch = this.contactCustomerSearch.trim()
      this.contactCustomerLoading = true
      try {
        const result = await requestAllCustomer({
          teamId: requestTeamId,
          type: 1,
          search: requestSearch,
          pageNo: 1,
          pageSize: 50
        })
        if (requestVersion !== this.contactCustomerRequestVersion || String(requestTeamId) !== String(this.teamId) || requestSearch !== this.contactCustomerSearch.trim()) {
          return
        }
        const records = result?.code === 200 ? result?.result?.records || [] : []
        const selectedCustomer = this.contactCustomerOptions.find(item => String(item?.id) === String(this.contactCustomerId))
        this.contactCustomerOptions = selectedCustomer && !records.some(item => String(item?.id) === String(selectedCustomer.id))
            ? [selectedCustomer, ...records]
            : records
        this.contactCustomerLoadedSearch = requestSearch
      } catch {
        if (requestVersion === this.contactCustomerRequestVersion && String(requestTeamId) === String(this.teamId)) {
          this.contactCustomerOptions = []
        }
      } finally {
        if (requestVersion === this.contactCustomerRequestVersion) {
          this.contactCustomerLoading = false
        }
      }
    },
    async handleContactCustomerChange(customerId) {
      const isChangedCustomer = String(customerId || '') !== String(this.contactPickerCustomerId || '')
      if (isChangedCustomer) {
        this.contactRequestVersion += 1
        this.contactPickerValues = []
        this.contactSelectionOptions = []
        this.contactOptions = []
      }
      this.contactPickerCustomerId = customerId || ''
      if (!customerId) {
        return
      }

      await this.loadContactsForCustomer(customerId)
    },
    async loadContactsForCustomer(customerId) {
      if (!customerId || !this.teamId) {
        this.contactRequestVersion += 1
        this.contactOptions = []
        return
      }
      const requestTeamId = this.teamId
      const requestCustomerId = String(customerId)
      const requestVersion = ++this.contactRequestVersion
      this.contactLoading = true
      try {
        const result = await getMailCustomerContactByCustomerId({customerId})
        if (requestVersion !== this.contactRequestVersion || String(requestTeamId) !== String(this.teamId) || String(requestCustomerId) !== String(this.contactPickerCustomerId)) {
          return
        }
        this.contactOptions = result?.code === 200 ? result?.result || [] : []
      } catch {
        if (requestVersion === this.contactRequestVersion && String(requestTeamId) === String(this.teamId)) {
          this.contactOptions = []
        }
      } finally {
        if (requestVersion === this.contactRequestVersion) {
          this.contactLoading = false
        }
      }
    },
    toContactSelectionOption(contact) {
      const contactId = contact?.id ?? contact?.contactId ?? contact?.value
      const value = contactId === undefined || contactId === null ? '' : String(contactId)
      const name = contact?.name || contact?.contactName || contact?.email || value
      const email = contact?.email ? ` (${contact.email})` : ''
      return {
        ...contact,
        value,
        label: `${name}${email}`
      }
    },
    getContactPickerOptions() {
      const known = [...this.contactSelectionOptions, ...this.contactOptions.map(contact => this.toContactSelectionOption(contact))]
      const optionMap = new Map()
      known.forEach(option => {
        if (option?.value) {
          optionMap.set(String(option.value), {...option, value: String(option.value)})
        }
      })
      return Array.from(optionMap.values())
    },
    updateContactPickerValues(rawValue) {
      const values = normalizeValueList(rawValue)
      const options = this.getContactPickerOptions()
      this.contactPickerValues = values
      this.contactSelectionOptions = values.map(value => {
        const option = options.find(item => item.value === value)
        return option || {value, label: value}
      })
    },
    confirmSelectedContacts() {
      const condition = this.form.conditions[this.contactSelectorConditionIndex]
      if (!condition) {
        return
      }
      const options = new Map(this.getConditionValueOptions(condition).map(option => [String(option.value), option]))
      this.contactSelectionOptions.forEach(option => {
        options.set(String(option.value), option)
      })
      this.updateConditionValues(condition, Array.from(options.keys()), Array.from(options.values()))
      condition.contactCustomerId = this.contactPickerCustomerId || ''
      const customer = this.contactCustomerOptions.find(item => String(item?.id) === String(this.contactPickerCustomerId))
      condition.contactCustomerLabel = customer ? this.getCustomerSelectionLabel(customer) : ''
      this.contactSelectorVisible = false
    },
    getConditionOptions(condition, index) {
      const optionMap = new Map(this.getConditionValueOptions(condition).map(option => [String(option.value), option]))
      const tagOptions = this.tagOptionsByCondition[index] || []
      tagOptions.forEach(option => {
        optionMap.set(String(option.value), option)
      })
      return Array.from(optionMap.values())
    },
    isTagLoading(index) {
      return this.tagLoadingConditionIndex === index
    },
    loadTagOptionsOnVisible(condition, index, visible) {
      if (visible && this.getFieldConfig(condition.field).picker === 'tag') {
        this.loadTagOptions(condition, index)
      }
    },
    async loadTagOptions(condition, index) {
      if (!this.teamId || this.isTagLoading(index)) {
        return
      }
      const config = this.getFieldConfig(condition.field)
      const requestTeamId = this.teamId
      const requestVersion = ++this.tagRequestVersion
      const requestField = condition.field
      this.tagLoadingConditionIndex = index
      try {
        let result
        if (config.tagType === 'level') {
          result = await getMailCustomerLevelList({teamId: requestTeamId, pageSize: 1000})
        } else {
          result = await mailCustomerLabelList({
            teamId: requestTeamId,
            labelType: config.tagType === 'source' ? 1 : 2,
            pageNo: 1,
            pageSize: 1000
          })
        }
        if (result?.code !== 200) {
          return
        }
        if (
          requestVersion !== this.tagRequestVersion ||
            String(requestTeamId) !== String(this.teamId) ||
            requestField !== this.form.conditions[index]?.field
        ) {
          return
        }
        const source = Array.isArray(result?.result) ? result.result : result?.result?.records || []
        const optionMap = new Map(this.getConditionOptions(condition, index).map(item => [String(item.value), item]))
        source.forEach(item => {
          const value = typeof item === 'string' ? item : item?.id ?? item?.value
          const label = typeof item === 'string'
              ? item
              : item?.labelName || item?.customerLevel || item?.name || item?.label || item?.value || item?.id
          if (value !== undefined && value !== null && String(value).trim()) {
            const normalizedValue = String(value)
            optionMap.set(normalizedValue, {
              ...(typeof item === 'string' ? {} : item),
              value: normalizedValue,
              label: String(label || value)
            })
          }
        })
        this.tagOptionsByCondition = {
          ...this.tagOptionsByCondition,
          [index]: Array.from(optionMap.values())
        }
      } catch {
        // Existing selections remain available when the option request fails.
      } finally {
        if (requestVersion === this.tagRequestVersion && this.tagLoadingConditionIndex === index) {
          this.tagLoadingConditionIndex = null
        }
      }
    },
    getForwardMemberEmail(member) {
      return String(member?.email || member?.mail || '')
    },
    getForwardMemberLabel(member) {
      const email = this.getForwardMemberEmail(member)
      const name = member?.userName || member?.username || member?.name || ''
      return name && email ? `${name} (${email})` : email || name
    },
    async loadForwardMembers(visible) {
      if (!visible || !this.teamId || this.forwardMemberLoading || String(this.forwardMembersLoadedTeamId) === String(this.teamId)) {
        return
      }
      const requestTeamId = this.teamId
      const requestVersion = ++this.forwardMemberRequestVersion
      this.forwardMemberLoading = true
      try {
        const members = []
        let pageNo = 1
        let totalPages = 1
        do {
          const result = await getTeamUserEmail({
            teamId: requestTeamId,
            pageNo,
            pageSize: 100,
            search: ''
          })
          if (requestVersion !== this.forwardMemberRequestVersion || String(requestTeamId) !== String(this.teamId)) {
            return
          }
          if (result?.code !== 200) {
            this.forwardMemberOptions = []
            return
          }
          members.push(...(result?.result?.records || []))
          totalPages = Math.max(1, Number(result?.result?.pages || 1))
          pageNo += 1
        } while (pageNo <= totalPages)

        const seenEmails = new Set()
        if (requestVersion !== this.forwardMemberRequestVersion || String(requestTeamId) !== String(this.teamId)) {
          return
        }
        this.forwardMemberOptions = members.filter(member => {
          const email = this.getForwardMemberEmail(member)
          if (!email || seenEmails.has(email)) {
            return false
          }
          seenEmails.add(email)
          return true
        })
        this.forwardMembersLoadedTeamId = requestTeamId
      } catch {
        if (requestVersion === this.forwardMemberRequestVersion && String(requestTeamId) === String(this.teamId)) {
          this.forwardMemberOptions = []
        }
      } finally {
        if (requestVersion === this.forwardMemberRequestVersion) {
          this.forwardMemberLoading = false
        }
      }
    },
    async createFolder() {
      const fileName = this.newFolderName.trim()
      if (!fileName) {
        ElMessage.warning(this.$t('account_setting.receive_rule.folder_name_placeholder'))
        return
      }
      if (!this.teamId || this.folderCreating) {
        return
      }

      const requestTeamId = this.teamId
      const requestVersion = ++this.folderCreateRequestVersion
      this.folderCreating = true
      try {
        const folderIdsBeforeCreate = new Set(this.folderOptions.map(item => String(item.value)))
        const result = await addEmailGroupFile({
          fileName,
          teamId: requestTeamId,
          isTop: 0
        })
        if (result?.code !== 200 || requestVersion !== this.folderCreateRequestVersion || String(requestTeamId) !== String(this.teamId)) {
          return
        }

        const userId = this.$store.state.user?.userInfo?.id
        if (userId) {
          await this.$store.dispatch('sidebar/getEmailGroupFileAllList', {teamId: requestTeamId, userId})
        }
        if (requestVersion !== this.folderCreateRequestVersion || String(requestTeamId) !== String(this.teamId)) {
          return
        }
        await this.$nextTick()

        const resultId = typeof result?.result === 'object' ? result?.result?.id : result?.result
        const hasResultId = resultId !== undefined && resultId !== null && String(resultId) !== ''
        const newOptions = this.folderOptions.filter(item => !folderIdsBeforeCreate.has(String(item.value)))
        const optionById = hasResultId
            ? this.folderOptions.find(item => String(item.value) === String(resultId))
            : null
        const optionByNewName = newOptions.find(item => item.label === fileName)
        const option = optionById || optionByNewName || (newOptions.length === 1 ? newOptions[0] : null)
        if (option && requestVersion === this.folderCreateRequestVersion) {
          this.form.targetFolder = option.value
        }
        this.newFolderName = ''
        if (result?.message && requestVersion === this.folderCreateRequestVersion) {
          ElMessage.success(result.message)
        }
      } catch {
        // The shared request layer keeps its existing error semantics.
      } finally {
        if (requestVersion === this.folderCreateRequestVersion) {
          this.folderCreating = false
        }
      }
    },
    validateConditions() {
      for (const condition of this.form.conditions) {
        if (this.isBooleanField(condition)) {
          if (!['yes', 'no'].includes(condition.operator)) {
            ElMessage.warning(this.$t('account_setting.receive_rule.condition_required'))
            return false
          }
          continue
        }

        const values = this.getConditionValues(condition)
        if (!values.length) {
          ElMessage.warning(this.$t('account_setting.receive_rule.condition_required'))
          return false
        }
        if (this.isEqualsOperator(condition) && values.length !== 1) {
          ElMessage.warning(this.$t('account_setting.receive_rule.only_one_email'))
          return false
        }
        if (this.isEqualsOperator(condition) && !EMAIL_PATTERN.test(values[0])) {
          ElMessage.warning(this.$t('account_setting.receive_rule.invalid_email'))
          return false
        }
      }
      return true
    },
    validateAction() {
      if (this.form.action === 'move' && !this.form.targetFolder) {
        ElMessage.warning(this.$t('account_setting.receive_rule.folder_required'))
        return false
      }
      if (this.form.action === 'mark' && !this.form.markActions.length) {
        ElMessage.warning(this.$t('account_setting.receive_rule.mark_required'))
        return false
      }
      if (this.form.action === 'forward') {
        const emails = normalizeValueList(this.form.forwardEmails)
        if (!emails.length) {
          ElMessage.warning(this.$t('account_setting.receive_rule.forward_required'))
          return false
        }
        if (emails.some(email => !EMAIL_PATTERN.test(email))) {
          ElMessage.warning(this.$t('account_setting.receive_rule.invalid_email'))
          return false
        }
      }
      return true
    },
    save(enabled) {
      if (!this.validateConditions() || !this.validateAction()) {
        return
      }

      const payload = JSON.parse(JSON.stringify(this.form))
      payload.conditions = payload.conditions.map(condition => {
        const values = this.getConditionValues(condition)
        return {
          ...condition,
          values,
          value: values.join(', ')
        }
      })
      payload.forwardEmails = normalizeValueList(payload.forwardEmails)
      payload.ruleName = payload.ruleName || this.defaultRuleName
      payload.enabled = enabled
      this.$emit('save', payload)
    }
  }
}
</script>

<style lang="less" scoped>
.rule-editor {
  color: #303133;
}

.rule-editor__section {
  margin-bottom: 20px;
}

.rule-editor__section-title {
  display: flex;
  align-items: center;
  min-height: 30px;
  font-size: 14px;
}

.rule-editor__required {
  margin-right: 3px;
  color: #f56c6c;
}

.rule-editor__join-select {
  width: 96px;
  margin-left: 8px;
}

.rule-editor__join-select,
.rule-editor__field-select,
.rule-editor__operator-select,
.rule-editor__action-select,
.rule-editor__folder-select {
  :deep(.el-select__wrapper) {
    min-height: 30px;
    height: 30px;
  }
}

.rule-editor__condition,
.rule-editor__action {
  display: flex;
  align-items: flex-start;
  min-height: 46px;
  padding: 7px 6px;
  background: #fafafa;
}

.rule-editor__condition + .rule-editor__condition {
  margin-top: 8px;
}

.rule-editor__field-select {
  flex: 0 0 142px;
}

.rule-editor__operator-select {
  flex: 0 0 130px;
  margin-left: 8px;
}

.rule-editor__condition-value,
.rule-editor__action-control {
  flex: 1;
  min-width: 150px;
  margin-left: 8px;
}

.rule-editor__condition-value {
  position: relative;

  .rule-editor__condition-value-select {
    width: 100%;
  }

  .rule-editor__condition-value-input {
    width: 100%;
    height: 30px;

    :deep(.el-input__wrapper) {
      box-sizing: border-box;
      height: 30px;
      min-height: 30px;
      align-items: center;
    }
  }

  :deep(.el-select__wrapper) {
    min-height: 30px;
    height: auto;
    align-items: center;
  }

  :deep(.el-select__selection) {
    flex-wrap: wrap;
  }
}

.rule-editor__action-control {
  :deep(.el-select__wrapper) {
    min-height: 30px;
    height: auto;
    align-items: center;
  }

  :deep(.el-select__selection) {
    flex-wrap: wrap;
  }
}

.rule-editor__condition-value--has-picker-trigger {
  :deep(.el-select__wrapper) {
    padding-right: 36px;
  }

  :deep(.el-select__caret) {
    display: none;
  }
}

.rule-editor__condition-picker-trigger {
  --el-button-text-color: #606266;
  --el-button-hover-text-color: #606266;
  --el-button-hover-bg-color: transparent;
  --el-button-active-text-color: #606266;
  --el-button-active-bg-color: transparent;
  --el-button-border-color: transparent;
  --el-button-hover-border-color: transparent;
  --el-button-active-border-color: transparent;
  position: absolute;
  top: 0;
  right: 0;
  width: 30px;
  min-width: 30px;
  height: 30px;
  min-height: 30px;
  margin: 0;
  padding: 0;
  color: #606266;

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    background-color: transparent !important;
    border-color: transparent !important;
    color: #606266 !important;
    box-shadow: none !important;
  }
}

.rule-editor__row-actions {
  display: flex;
  flex: 0 0 62px;
  gap: 4px;
  margin-left: 4px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}

.rule-editor__action-select {
  flex: 0 0 142px;
}

.rule-editor__folder-select {
  width: 100%;

  :deep(.el-select__wrapper) {
    align-items: center;
    background: #fff;
  }

  :deep(.el-select__prefix) {
    color: #606266;
  }
}

.rule-editor__folder-prefix {
  display: inline-flex;
}

.rule-editor__folder-option {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.rule-editor__folder-action {
  flex: 1;
  min-width: 150px;
  margin-left: 8px;
}

.rule-editor__folder-create {
  display: flex;
  padding: 8px 10px;
  border-top: 1px solid #ebeef5;
}

.rule-editor__folder-create-input {
  flex: 1;
}

.rule-editor__folder-create-input :deep(.el-input__wrapper) {
  min-height: 30px;
}

.rule-editor__folder-create-input :deep(.el-button) {
  min-height: 20px;
  padding: 0 2px;
}

.rule-editor__folder-create--fallback {
  margin-top: 8px;
}

.rule-editor__mark-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rule-editor__name {
  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
  }
}

.rule-editor__name-input {
  padding: 7px 6px;
  background: #fafafa;

  :deep(.el-input__wrapper) {
    min-height: 30px;
  }
}

.customer-picker {
  display: flex;
  gap: 38px;
}

.customer-picker__panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  height: 348px;
  padding: 10px 12px;
  box-sizing: border-box;
  background: #fafafa;
}

.customer-picker__search {
  cursor: pointer;
  color: #c0c4cc;

  &:hover {
    color: #409eff;
  }
}

.customer-picker__panel > .el-input {
  flex: 0 0 auto;
  margin-bottom: 8px;

  :deep(.el-input__wrapper) {
    min-height: 36px;
  }
}

.customer-picker__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.customer-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 29px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #303133;
  font: inherit;
  font-size: 14px;
  line-height: 29px;
  text-align: left;
  cursor: pointer;

  > :first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > .el-icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    margin-left: 8px;
    border-radius: 1px;
    background: #409eff;
    color: #fff;

    :deep(svg) {
      width: 9px;
      height: 9px;
    }
  }

  &:hover {
    color: #409eff;
  }

  &:disabled {
    cursor: default;
    color: #909399;

    > .el-icon {
      background: #c0c4cc;
    }
  }
}

.customer-picker__customer-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-picker__customer-separator {
  padding: 0 4px;
  font-weight: 500;
}

.customer-picker__customer-company {
  font-weight: 600;
}

.customer-picker__item--selected {
  cursor: default;

  :deep(.el-button) {
    --el-button-text-color: #fff;
    --el-button-hover-text-color: #fff;
    --el-button-hover-bg-color: #f56c6c;
    --el-button-active-text-color: #fff;
    --el-button-active-bg-color: #f56c6c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    min-height: 14px;
    margin-left: 8px;
    padding: 0;
    border-radius: 50%;
    background: #f56c6c;

    .el-icon,
    svg {
      width: 9px;
      height: 9px;
    }
  }
}

.customer-picker__selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 28px;
  margin-bottom: 8px;
  color: #303133;
  font-size: 14px;

  em {
    color: #409eff;
    font-style: normal;
  }

  :deep(.el-button) {
    min-height: 28px;
    padding: 0;
    font-size: 14px;
  }
}

.customer-picker__list :deep(.el-empty) {
  height: 100%;
  padding: 0;
}

.rule-picker-form {
  display: flex;
  flex-direction: column;
  gap: 8px;

  > label {
    margin-top: 4px;
    color: #303133;
    font-size: 14px;
  }

  :deep(.el-select__wrapper) {
    min-height: 30px;
    height: auto;
  }

  :deep(.el-select__selection) {
    flex-wrap: wrap;
  }
}

.rule-picker-dialog__select {
  width: 100%;

  :deep(.el-select__wrapper) {
    min-height: 30px;
    height: auto;
  }

  :deep(.el-select__selection) {
    flex-wrap: wrap;
  }
}

:global(.receive-rule-dialog .el-dialog__body) {
  max-height: 68vh;
  overflow-y: auto;
}

:global(.receive-rule-folder-dropdown .el-select-dropdown__footer) {
  padding: 0;
}

:global(.customer-picker-dialog .el-dialog__body) {
  padding: 0px 8px;
}

:global(.customer-picker-dialog .el-dialog__header) {
  margin-right: 0;
  //border-bottom: 1px solid #f0f0f0;
}

:global(.customer-picker-dialog .el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
}

:global(.customer-picker-dialog .el-dialog) {
  width: min(780px, calc(100vw - 32px)) !important;
}

:global(.customer-picker-dialog .el-dialog__footer),
:global(.rule-picker-dialog .el-dialog__footer) {
  display: flex;
  justify-content: center;
  text-align: center;
}

@media (max-width: 760px) {
  .rule-editor__condition,
  .rule-editor__action {
    flex-wrap: wrap;
    gap: 8px 0;
  }

  .rule-editor__field-select,
  .rule-editor__operator-select {
    flex: 1 1 calc(50% - 4px);
  }

  .rule-editor__field-select {
    margin-left: 0;
  }

  .rule-editor__condition-value,
  .rule-editor__action-control,
  .rule-editor__folder-action {
    flex-basis: calc(100% - 72px);
  }

  .customer-picker {
    flex-direction: column;
    gap: 12px;
  }

  .customer-picker__panel {
    height: 260px;
  }
}
</style>
